<template>
  <!-- A focused card fills its 2x2 grid area so the gallery has leftover height to absorb. -->
  <div
    class="relative f-col"
    :class="{ 'h-full': isFocused }"
    data-test-id="product-card"
  >
    <!-- Quickshop CTA -->
    <base-button
      v-if="showQuickshop && !isComingSoon && !isRobisOnly"
      :aria-describedby="product.id"
      aria-haspopup="dialog"
      :aria-label="$t.add"
      class="absolute right-0 top-0 z-3 p-2"
      data-test-id="product-card-add"
      @click="openQuickshop"
    >
      <span class="h-6 w-6 flex center rounded-xs bg-white bg-op-75 p-1 shadow-xs backdrop-blur-0.75 duration" :class="brandClasses.quickshopCta">
        <vf-icon name="plus" size="xs" />
      </span>
    </base-button>

    <!-- Product Image -->
    <div v-if="galleryPending" class="aspect-square w-full skeleton" />
    <div
      v-else
      class="relative w-full"
      :class="{ 'flex-1 f-col': isFocused }"
    >
      <product-card-gallery
        ref="galleryRef"
        :asset-override
        :class="brandClasses.gallery"
        :fetchpriority
        :focused
        :items="activeGallery"
        :lazy
        :media-size
        :name="data.name"
        :show-secondary-media="showSecondaryMedia || showMobileCarousel"
      >
        <template #badge>
          <product-badge
            v-if="badgeLabel"
            :class="badgePosition === 'bottom' ? 'bottom-0' : 'top-0'"
            data-test-id="product-card-badge"
            :label="badgeLabel"
          />
        </template>
        <template #link>
          <base-link aria-hidden="true" class="absolute-0" tabindex="-1" :to="url" @click.capture="navigateToPDP" />
        </template>
      </product-card-gallery>
      <!--
        Always-visible overlay link scoped to gallery wrapper.
        Lives outside Gallery.vue slot system so it's never hidden by hover/carousel state.
        Also drives secondary media on desktop hover.
      -->
      <base-link
        v-if="!$feature.enableProductCardGalleryCarousel"
        aria-hidden="true"
        class="absolute-0"
        tabindex="-1"
        :to="url"
        @click.capture="navigateToPDP"
        @mouseenter="showSecondaryMedia = $viewport.lg"
        @mouseleave="showSecondaryMedia = false"
      />
    </div>

    <div class="f-col gap-3 px-2 pb-3">
      <!-- Color Chips: single line, +N navigates to PDP -->
      <product-swatches
        v-if="swatches.length"
        v-model="swatchModel"
        :lazy
        :swatches
        @more="navigateToPDP"
      />

      <!-- Text block: Eyebrow, Title, Description -->
      <div class="f-col gap-1">
        <!-- Eyebrow -->
        <div
          v-if="showEyebrow && data.eyebrow?.label"
          class="line-clamp-1 overline-2 c-grey-40 -mb-1"
          :class="brandClasses.eyebrow"
          data-test-id="product-card-eyebrow"
        >
          {{ data.eyebrow.label }}
        </div>

        <!-- Product Title -->
        <base-link
          :id="product.id"
          class="line-clamp-2 body-2 c-grey-20 fw-bold"
          data-test-id="product-card-title"
          :to="url"
          @click.capture="navigateToPDP"
        >
          {{ data.name }}
        </base-link>

        <!-- Short Description -->
        <div
          v-if="$feature.showShortDescriptionInProductList && data.shortDescription"
          class="caption-1 c-grey-40"
          data-test-id="product-card-short-description"
        >
          {{ data.shortDescription }}
        </div>

        <!-- rating -->
        <base-link
          v-if="!hideRating && data?.rating?.reviewsCount"
          :aria-label="replaceAll($t.ratingLabel, { value: data?.rating?.score?.toFixed(1), max: 5 })"
          class="items-center gap-0.5 c-black i-flex"
          :to="url"
          @click.capture="navigateToPDP"
        >
          <span class="overline-2">{{ data?.rating?.score?.toFixed(1) }}</span>
          <vf-icon aria-hidden="true" name="star-filled" />
        </base-link>
      </div>

      <!-- Product Price -->
      <product-pricing
        class="lh-1 o:children:overline-1"
        class-current="fw-bold"
        :currency="data.currency"
        data-test-id="product-card-pricing"
        :price="data.price"
        :show-discount-percentage
      />
    </div>

    <panel-quickshop v-if="showQuickshop" v-slot="props" :title="$t.size">
      <dialog-quickshop v-bind="props" :product-id="swatchModel" />
    </panel-quickshop>
  </div>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#commerce/components/product/card/Card.vue'
import type { Product } from '#root/api/clients/product/data-contracts'
import type { ProductClickAction } from '#types/gtm'
import type { ColorOption } from '#types/product'
import { GalleryContextKey } from '#commerce/contexts/gallery'

const {
  breadcrumbs = [],
  focused,
  imageFetchPriority: fetchpriority,
  trackingOffset,
  product,
  showQuickshop = true,
  storeFilterState,
  trackingCategory,
  trackingList
} = defineProps<Props>()
const emit = defineEmits<Emits>()

const { isFocused } = useProductCardMedia(() => focused)

const { $feature, $gtm, $viewport } = useNuxtApp()
const origin = useOrigin()
const route = useRoute()
const router = useRouter()
const { getIsComingSoonProduct } = useComingSoon()

const { badgePosition, brandClasses, mediaSize } = useAppConfig().components.product.card

const PanelQuickshop = createDialog('quickshop', { type: { sm: 'panel', lg: 'modal' } })

const galleryRef = useTemplateRef('galleryRef')
const showSecondaryMedia = ref(false)
const showMobileCarousel = ref(false)

// AC: long press on mobile shows secondary image when the carousel feature is disabled.
onLongPress(
  computed(() => galleryRef.value?.$el ?? null),
  () => {
    if (!$viewport.lg && !$feature.enableProductCardGalleryCarousel)
      showSecondaryMedia.value = true
  },
  { delay: 500 }
)

watch(() => $viewport.lg, (isLargeViewport) => {
  showMobileCarousel.value = $feature.enableProductCardGalleryCarousel && !isLargeViewport
}, { immediate: true })

const productUrl = product.url
const defaultColor = getDefaultColorOption(product.attributes, product.id) || {}
const activeProduct = ref(defaultColor.id || product.id)
const swatchId = ref(defaultColor.id || product.id)

const showEyebrow = computed(() => {
  const { mode, routes } = $feature.eyebrowVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  return mode === 'show' ? match : !match
})

const productDetailsKey = computed(() => `productDetails-${activeProduct.value}`)
const { data } = useApi().products.details(activeProduct, {}, {
  immediate: false,
  key: productDetailsKey,
  default: () => ({
    ...product,
    url: defaultColor.url || productUrl,
    eyebrow: formatBadgeOrEyebrow(
      defaultColor.eyebrow || product.eyebrow,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    ),
    badge: formatBadgeOrEyebrow(
      defaultColor.badge || product.badge,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
  }),
  transform: (input) => ({
    ...input,
    eyebrow: formatBadgeOrEyebrow(
      input.eyebrow,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    ),
    badge: formatBadgeOrEyebrow(
      input.badge,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
  })
}) as { data: Ref<Product>, pending: Ref<boolean> }

const categoryURL = breadcrumbs[breadcrumbs.length - 1]?.url
const url = computed(() => {
  if (data.value?.url && storeFilterState) {
    const baseUrl = `${data.value.url.startsWith('/') ? origin : ''}${data.value.url}`
    const parsedUrl = new URL(baseUrl)
    parsedUrl.searchParams.append('storeFilter', storeFilterState)
    return `${parsedUrl.pathname}${parsedUrl.search}`
  }
  return data.value?.url
})

const isComingSoon = computed(() =>
  $feature.showComingSoonOnQuickshop && getIsComingSoonProduct(data.value.badge, data.value.eyebrow)
)
const isRobisOnly = computed(() => $feature.allowProductReservationOnPDP && data.value?.isRobisOnly)

const swatches = (product.attributes?.find(({ type }) => type === 'color')?.options || []) as ColorOption[]

const swatchModel = $feature.enablePlpSwatchDetails ? swatchId : activeProduct

const galleryContextKey = inject(GalleryContextKey)
const selectedSwatch = computed(() => swatches.find(({ id }) => id === swatchModel.value))

const { activeGallery, galleryPending } = useActiveGallery(computed(() => ({
  id: swatchModel.value,
  gallery: selectedSwatch.value?.gallery || data.value?.gallery,
  altGalleries: selectedSwatch.value?.altGalleries
})), galleryContextKey)

const getColorSwatch = (id: string) => swatches.find((s) => s.id === id)

watch(swatchId, (id) => {
  if (!$feature.enablePlpSwatchDetails) return
  const colorSwatch = getColorSwatch(id)
  if (colorSwatch) {
    data.value.badge = formatBadgeOrEyebrow(
      colorSwatch.badge,
      product.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
    data.value.eyebrow = formatBadgeOrEyebrow(
      colorSwatch.eyebrow,
      product.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
    data.value.price = colorSwatch.price
    data.value.gallery = colorSwatch.gallery
    data.value.url = colorSwatch.url
  }
})

const badgeLabel = computed(() => {
  const { mode, routes } = $feature.badgeVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  const isVisible = mode === 'show' ? match : !match
  return !!data.value.badge && isVisible && 'label' in data.value.badge && data.value.badge.label
})

const handleGtmEvent = (payload, action: ProductClickAction) =>
  $gtm.push('product.onProductClick', payload, {
    action,
    breadcrumbs,
    category: trackingCategory,
    list: trackingList,
    offset: trackingOffset,
    searchTerm: route.query.q?.toString()
  })

const navigateToPDP = (e?: PointerEvent) => {
  handleGtmEvent(data.value, 'Navigate To PDP')
  emit('product-click', product)

  const serializedBreadcrumbs = serializeAnalyticsBreadcrumbs(breadcrumbs)
  const state = {
    breadcrumbs: serializedBreadcrumbs,
    category: trackingCategory,
    list: trackingList,
    searchTerm: route.query.q,
    categoryURL,
    galleryContext: galleryContextKey?.value
  }

  const destination = url.value
  if (!destination) return

  if (e?.metaKey || e?.ctrlKey) {
    localStorage.setItem(`from_plp_${data.value.id}`, JSON.stringify(state))
  }
  else {
    e?.preventDefault()
    router.push({
      path: destination.split('?')[0],
      query: Object.fromEntries(new URLSearchParams(destination.split('?')[1])),
      state
    })
  }
}

const openQuickshop = () => {
  PanelQuickshop.open({
    product: data.value,
    categoryURL,
    gtmConfig: {
      ...(trackingCategory && { category: trackingCategory }),
      ...(trackingList && { list: trackingList }),
      ...(storeFilterState && { shippingMethod: 'BOPIS_PLP_SD', shippingStoreId: storeFilterState, shippingWindow: '24Hrs' })
    }
  })
  handleGtmEvent(data.value, 'Open Quick Shop')
}
</script>
